/**
 * LA CRONOLOGIA DEI TURNI (BACKGAMMON-UX-20261031 rev.2 punti 1-3; cantiere qgammon-orcb-30). Parte PURA: niente React, niente socket.
 *
 * ⛔ IL CLIENT NON INVENTA NIENTE. I turni vengono da DUE fonti del server e da nessun'altra:
 *   - `match:history` (types 0.23, contratto ritmo-bot §5): i turni GIOCATI della game corrente, a ogni `match:join`; una riconnessione
 *     SOSTITUISCE la storia, non la somma (`riduciCronologia`, evento `storia`);
 *   - poi `match:moveApplied` (che board-ui tiene per se': la TMA lo legge da `lastMove`) coi suoi `steps`, e i dadi da `match:dice` (`lastRoll`).
 * Un ply che manca (la storia non e' arrivata, o due mosse nello stesso render) diventa un `TurnoMancante`: la scheda scrive il MOTIVO, mai un
 * turno ricostruito. Nessun giudizio qui dentro: il voto e la mossa suggerita sono del coach (`coach:history`/`coach:verdict`), e si cercano per
 * chiave (`verdettoDelTurno`).
 *
 * ⛔ IN PRODUZIONE QUESTO MODULO NON ENTRA. Lo usa `BackgammonMatch` solo dentro il confronto LETTERALE del flag del coach, e il chunk `CoachUi`
 * (che in produzione non esiste).
 */
import type { CoachVerdictPayload, MatchHistoryPayload } from '@qlashgeminiz/qgammon-types';

export type Colore = 'W' | 'B';

/** Un movimento come l'ha giocato il server. `hit === null` = il server non l'ha detto (un emettitore piu' vecchio di 0.23, senza `steps`). */
export interface Passo {
  from: number | 'bar';
  to: number | 'off';
  die: number;
  hit: boolean | null;
}

export interface TurnoConcluso {
  tipo: 'turno';
  chiave: string;
  matchId: string;
  gameNumber: number;
  ply: number;
  player: Colore;
  /** I dadi di quel ply; `null` = il server non li ha mandati (partita ripresa) o non sono arrivati. */
  dadi: readonly [number, number] | null;
  /** `[]` = nessuna mossa. */
  passi: readonly Passo[];
  /**
   * Il cubo SUBITO DOPO questo turno, come l'ha mandato `match:history`; assente o `null` = non si sa (il turno e' arrivato da `match:moveApplied`,
   * che il cubo non lo porta). Il tavolo storico NON mostra mai il cubo di adesso: senza questo dato il cubo non si disegna (D3 della revisione).
   */
  cubo?: { value: number; owner: Colore | null } | null;
}

/** Un ply di cui la TMA non ha il turno: si mostra il motivo, non un turno inventato. */
export interface TurnoMancante {
  tipo: 'mancante';
  chiave: string;
  matchId: string;
  gameNumber: number;
  ply: number;
}

export type VoceCronologia = TurnoConcluso | TurnoMancante;

export interface StatoCronologia {
  matchId: string | null;
  gameNumber: number;
  /** In ordine di ply, senza buchi da 1 all'ultimo ply concluso. */
  voci: readonly VoceCronologia[];
  /** I dadi visti per ply (da `match:dice`), per i turni che arrivano dopo la storia. */
  dadi: Readonly<Record<number, readonly [number, number]>>;
  /** Il server ha mandato `match:history` per questa game. */
  daServer: boolean;
}

export const CRONOLOGIA_VUOTA: StatoCronologia = { matchId: null, gameNumber: 0, voci: [], dadi: {}, daServer: false };

export function chiaveTurno(matchId: string, gameNumber: number, ply: number): string {
  return `${matchId}|${gameNumber}|${ply}`;
}

/** Cio' che serve di `match:moveApplied` (la forma di `lastMove` di board-ui). */
export interface MossaApplicata {
  matchId: string;
  ply: number;
  player: Colore;
  moves: readonly { from: number | 'bar'; to: number | 'off'; die: number }[];
  gameNumber?: number;
  steps?: readonly { from: number | 'bar'; to: number | 'off'; die: number; hit: boolean }[];
}

/** Cio' che serve di `match:dice` (la forma di `lastRoll` di board-ui). */
export interface DadiTirati {
  matchId?: string;
  gameNumber?: number;
  ply: number;
  roll: { d1: number; d2: number };
}

export type EventoCronologia =
  | { tipo: 'partita'; matchId: string | null; gameNumber: number }
  | { tipo: 'storia'; payload: MatchHistoryPayload }
  | { tipo: 'mossa'; payload: MossaApplicata; gameNumber: number }
  | { tipo: 'dadi'; payload: DadiTirati; gameNumber: number };

function nuovaGame(matchId: string | null, gameNumber: number): StatoCronologia {
  return { matchId, gameNumber, voci: [], dadi: {}, daServer: false };
}

/** Riempie i buchi fra 1 e l'ultimo ply con `TurnoMancante`. `turni` e' in ordine di ply, senza duplicati. */
function senzaBuchi(matchId: string, gameNumber: number, turni: readonly VoceCronologia[]): VoceCronologia[] {
  const out: VoceCronologia[] = [];
  let atteso = 1;
  for (const t of turni) {
    for (; atteso < t.ply; atteso++) out.push({ tipo: 'mancante', chiave: chiaveTurno(matchId, gameNumber, atteso), matchId, gameNumber, ply: atteso });
    out.push(t);
    atteso = t.ply + 1;
  }
  return out;
}

function ultimoPly(s: StatoCronologia): number {
  return s.voci.length > 0 ? s.voci[s.voci.length - 1].ply : 0;
}

/**
 * Il riduttore. Regole:
 *  - `partita`: un'altra partita o una game SUCCESSIVA = una cronologia nuova e vuota (la game corrente e basta: e' cio' che il server manda).
 *    Un evento di una game PRECEDENTE della stessa partita (un `lastMove` rimasto) non torna indietro: si ignora.
 *  - `storia`: SOSTITUISCE i turni fino al suo ultimo ply; i turni arrivati dopo (ply piu' alto, stessa game) restano.
 *  - `mossa`: aggiunge il turno se il suo ply e' oltre l'ultimo; un ply gia' visto si ignora (un doppione dopo una riconnessione).
 *  - `dadi`: ricorda i dadi del ply, e li mette sul turno se c'e' gia' senza dadi.
 */
export function riduciCronologia(s: StatoCronologia, e: EventoCronologia): StatoCronologia {
  switch (e.tipo) {
    case 'partita':
      if (e.matchId === s.matchId && e.gameNumber <= s.gameNumber) return s;
      return nuovaGame(e.matchId, e.gameNumber);
    case 'storia': {
      const h = e.payload;
      if (h.matchId === s.matchId && h.gameNumber < s.gameNumber) return s;
      const base = h.matchId === s.matchId && h.gameNumber === s.gameNumber ? s : nuovaGame(h.matchId, h.gameNumber);
      const turni: TurnoConcluso[] = [...h.turns]
        .sort((a, b) => a.ply - b.ply)
        .filter((t, i, arr) => t.ply >= 1 && (i === 0 || arr[i - 1].ply !== t.ply))
        .map((t) => ({
          tipo: 'turno',
          chiave: chiaveTurno(h.matchId, h.gameNumber, t.ply),
          matchId: h.matchId,
          gameNumber: h.gameNumber,
          ply: t.ply,
          player: t.player,
          dadi: t.dice ? [t.dice[0], t.dice[1]] : null,
          passi: t.steps.map((p) => ({ from: p.from, to: p.to, die: p.die, hit: p.hit })),
          cubo: { value: t.cube.value, owner: t.cube.owner },
        }));
      const ultimo = turni.length > 0 ? turni[turni.length - 1].ply : 0;
      const dopo = base.voci.filter((v) => v.ply > ultimo && v.tipo === 'turno');
      return { ...base, voci: senzaBuchi(h.matchId, h.gameNumber, [...turni, ...dopo]), daServer: true };
    }
    case 'mossa': {
      const m = e.payload;
      const gameNumber = m.gameNumber ?? e.gameNumber;
      if (m.ply < 1 || (m.matchId === s.matchId && gameNumber < s.gameNumber)) return s;
      const base = m.matchId === s.matchId && gameNumber === s.gameNumber ? s : nuovaGame(m.matchId, gameNumber);
      if (m.ply <= ultimoPly(base)) return base;
      const passi: Passo[] = m.steps
        ? m.steps.map((p) => ({ from: p.from, to: p.to, die: p.die, hit: p.hit }))
        : m.moves.map((p) => ({ from: p.from, to: p.to, die: p.die, hit: null }));
      const turno: TurnoConcluso = {
        tipo: 'turno',
        chiave: chiaveTurno(m.matchId, gameNumber, m.ply),
        matchId: m.matchId,
        gameNumber,
        ply: m.ply,
        player: m.player,
        dadi: base.dadi[m.ply] ?? null,
        passi,
      };
      return { ...base, voci: senzaBuchi(m.matchId, gameNumber, [...base.voci, turno]) };
    }
    case 'dadi': {
      const d = e.payload;
      const gameNumber = d.gameNumber ?? e.gameNumber;
      if ((d.matchId !== undefined && d.matchId !== s.matchId) || gameNumber !== s.gameNumber) return s;
      const coppia = [d.roll.d1, d.roll.d2] as const;
      const voci = s.voci.map((v) => (v.tipo === 'turno' && v.ply === d.ply && v.dadi === null ? { ...v, dadi: coppia } : v));
      return { ...s, dadi: { ...s.dadi, [d.ply]: coppia }, voci };
    }
  }
}

/* ─────────────────────────────── La posizione di ogni turno: all'indietro dallo stato del server ─────────────────────────────── */

export interface Posizione {
  points: readonly number[];
  bar: { W: number; B: number };
  off: { W: number; B: number };
}

/**
 * Il tavolo PRIMA di un turno, dato il tavolo DOPO: si disfano i passi dall'ultimo al primo. Positivo = pedine bianche, negativo = nere
 * (la convenzione del motore e di `PreviewPosition`). `null` se non si puo' fare con certezza: un passo senza `hit` (server senza `steps`)
 * o una posizione che non torna (una pedina che non c'e'). Mai un tavolo indovinato.
 */
export function disfaTurno(dopo: Posizione, turno: Pick<TurnoConcluso, 'player' | 'passi'>): Posizione | null {
  const s = turno.player === 'W' ? 1 : -1;
  const io = turno.player;
  const lui: Colore = io === 'W' ? 'B' : 'W';
  const points = [...dopo.points];
  const bar = { ...dopo.bar };
  const off = { ...dopo.off };
  for (let i = turno.passi.length - 1; i >= 0; i--) {
    const p = turno.passi[i];
    if (p.hit === null) return null;
    if (p.to === 'off') {
      if (off[io] < 1) return null;
      off[io] -= 1;
    } else {
      if (p.to < 0 || p.to > 23 || points[p.to] * s < 1) return null;
      points[p.to] -= s;
      if (p.hit) {
        if (points[p.to] !== 0 || bar[lui] < 1) return null;
        points[p.to] = -s;
        bar[lui] -= 1;
      }
    }
    if (p.from === 'bar') bar[io] += 1;
    else {
      if (p.from < 0 || p.from > 23 || points[p.from] * s < 0) return null;
      points[p.from] += s;
    }
  }
  return { points, bar, off };
}

/**
 * Il tavolo DOPO ogni turno concluso, per ply, a partire dal tavolo del server (`base`, che e' quello dopo il turno `plyBase`). I turni oltre
 * `plyBase` (arrivati prima dello stato) e quelli prima di un buco o di un passo senza `hit` hanno `null`.
 */
export function posizioniDopo(voci: readonly VoceCronologia[], base: Posizione | null, plyBase: number): Record<number, Posizione | null> {
  const out: Record<number, Posizione | null> = {};
  // D6: lo stato del server e' «dopo il turno plyBase» solo se quel turno c'e' fra le voci. Se manca (lo stato e' arrivato prima della mossa, o la
  // mossa prima dello stato), disfare da `base` darebbe al turno N-1 il tavolo dopo N: nessun tavolo, mai uno sbagliato.
  let cur: Posizione | null = voci.some((v) => v.ply === plyBase) ? base : null;
  for (let i = voci.length - 1; i >= 0; i--) {
    const v = voci[i];
    if (v.ply > plyBase) {
      out[v.ply] = null;
      continue;
    }
    out[v.ply] = cur;
    cur = cur && v.tipo === 'turno' ? disfaTurno(cur, v) : null;
  }
  return out;
}

/* ─────────────────────────────── Il testo della mossa, nella numerazione di chi guarda ─────────────────────────────── */

/** Il numero di un punto per chi GUARDA (P3.4: una numerazione sola per tutta la scheda). Il motore numera come il Bianco. */
export function puntoPer(chiGuarda: Colore, idx: number): number {
  return chiGuarda === 'W' ? idx + 1 : 24 - idx;
}

export type Movimento =
  | { tipo: 'punti'; da: number; a: number; colpisce: boolean }
  | { tipo: 'entra'; a: number; colpisce: boolean }
  | { tipo: 'fuori'; da: number }
  | { tipo: 'entraFuori' };

/** I movimenti di un turno, in ordine di gioco, nella numerazione di `chiGuarda`. `colpisce` e' vero solo se il server l'ha detto. */
export function movimenti(passi: readonly Passo[], chiGuarda: Colore): Movimento[] {
  return passi.map((p): Movimento => {
    const colpisce = p.hit === true;
    if (p.from === 'bar') return p.to === 'off' ? { tipo: 'entraFuori' } : { tipo: 'entra', a: puntoPer(chiGuarda, p.to), colpisce };
    if (p.to === 'off') return { tipo: 'fuori', da: puntoPer(chiGuarda, p.from) };
    return { tipo: 'punti', da: puntoPer(chiGuarda, p.from), a: puntoPer(chiGuarda, p.to), colpisce };
  });
}

/* ─────────────────────────────── Il coach nella cronologia ─────────────────────────────── */

/**
 * ⛔ LA GUARDIA DEL COMPETITIVO (V7 a) di tma-orcr, 01/11 22:05Z; la forma l'ha data qgammon-orcb alle 22:09Z). UN punto solo.
 * Il coach compare solo in una partita che il server dichiara FUORI classifica (`rated === false`) e NON ordinata da un servizio (torneo,
 * sfida via qTournaments: `serviceOrdered === false`). Assente o `null` = non lo so = competitivo = nessun nodo del coach. E' la regola di Luca
 * («resta fuori da tornei/competitive»), non una copia del predicato del server: anche con `allowed` vero, una partita competitiva non ha coach.
 */
export function partitaFuoriCompetitivo(state: { rated?: boolean | null; serviceOrdered?: boolean | null } | null | undefined): boolean {
  return !!state && state.rated === false && state.serviceOrdered === false;
}

/**
 * Il voto e la mossa suggerita di UN turno: il verdetto del server per quella partita, game e ply, del MIO posto, e SOLO se il turno e'
 * concluso (sta nella cronologia). Fra piu' versioni, l'ultima per `version`. Un verdetto con la chiave della mossa in corso non ha un turno
 * concluso a cui attaccarsi: non si mostra mai (V7 b).
 */
export function verdettoDelTurno(
  verdetti: readonly CoachVerdictPayload[],
  voce: VoceCronologia | null,
  mioPosto: Colore | null,
): CoachVerdictPayload | null {
  if (!voce || voce.tipo !== 'turno' || mioPosto === null || voce.player !== mioPosto) return null;
  let trovato: CoachVerdictPayload | null = null;
  for (const v of verdetti) {
    if (v.matchId !== voce.matchId || v.gameNumber !== voce.gameNumber || v.ply !== voce.ply || v.player !== mioPosto) continue;
    if (!trovato || v.version > trovato.version) trovato = v;
  }
  return trovato;
}

/** La voce selezionata, PER CHIAVE. Se la chiave non c'e' piu' (riconnessione con un'altra storia): l'ultima voce (P3.14), o `null`. */
export function voceSelezionata(voci: readonly VoceCronologia[], chiave: string | null): VoceCronologia | null {
  if (voci.length === 0) return null;
  if (chiave !== null) {
    const v = voci.find((x) => x.chiave === chiave);
    if (v) return v;
  }
  return voci[voci.length - 1];
}

/** L'ultimo MIO turno concluso col voto (l'entrata dalla riga del coach apre li'), o l'ultimo turno. */
export function chiaveDiEntrata(voci: readonly VoceCronologia[], verdetti: readonly CoachVerdictPayload[], mioPosto: Colore | null): string | null {
  for (let i = voci.length - 1; i >= 0; i--) if (verdettoDelTurno(verdetti, voci[i], mioPosto)) return voci[i].chiave;
  return voci.length > 0 ? voci[voci.length - 1].chiave : null;
}

/**
 * V7 b: dei verdetti si mostrano solo quelli dei turni CONCLUSI. Un turno e' concluso quando il server l'ha applicato: il suo ply e' entro il
 * contatore del server (`state.ply`) della game corrente, o e' di una game precedente. Un verdetto con la chiave della mossa in corso (arrivato
 * prima, per un difetto o un ordine diverso) resta fuori: anche dalla riga chiusa del coach, che mostra «l'ultimo voto».
 */
export function soloTurniConclusi<V extends Pick<CoachVerdictPayload, 'gameNumber' | 'ply'>>(verdetti: readonly V[], gameNumber: number, plyServer: number): readonly V[] {
  const out = verdetti.filter((v) => v.gameNumber < gameNumber || (v.gameNumber === gameNumber && v.ply <= plyServer));
  return out.length === verdetti.length ? verdetti : out;
}

/**
 * L'allenamento fra amici REVOCATO (V7 a rev.3 di tma-orcr, 22:26Z; il campo e' del server, disegno 31 §3.5: `match:state.allenamento =
 * null | { stato: 'attivo' | 'revocato', revocatoDa? }`, non ancora nei tipi 0.23: si legge come dato sconosciuto). Revocare ferma l'analisi,
 * non cancella quella consegnata: la cronologia mostra al proprietario i giudizi GIA' consegnati, con la scritta, e nessuno nuovo.
 */
export function allenamentoRevocato(state: unknown): boolean {
  if (!state || typeof state !== 'object') return false;
  const a = (state as { allenamento?: unknown }).allenamento;
  return !!a && typeof a === 'object' && (a as { stato?: unknown }).stato === 'revocato';
}

/**
 * D7: dopo la revoca, il coach nella cronologia (giudizi, segnaposto, scritta) e' solo di un posto che il coach l'aveva ACCESO: acceso al momento
 * della revoca, o con almeno un giudizio consegnato per quel posto.
 */
export function coachAccesoPerIlPosto(accesoAllaRevoca: boolean, verdetti: readonly Pick<CoachVerdictPayload, 'player'>[], mioPosto: Colore | null): boolean {
  return accesoAllaRevoca || (mioPosto !== null && verdetti.some((v) => v.player === mioPosto));
}

/** Dopo la revoca: i verdetti visti alla revoca piu' quelli dell'ultima `coach:history`, senza doppioni; MAI un `coach:verdict` arrivato dopo. */
export function verdettiDopoRevoca<V extends Pick<CoachVerdictPayload, 'matchId' | 'gameNumber' | 'ply' | 'version'>>(allaRevoca: readonly V[], storia: readonly V[]): V[] {
  const visti = new Set<string>();
  const out: V[] = [];
  for (const v of [...allaRevoca, ...storia]) {
    const k = `${v.matchId}|${v.gameNumber}|${v.ply}|${v.version}`;
    if (visti.has(k)) continue;
    visti.add(k);
    out.push(v);
  }
  return out.sort((a, b) => a.gameNumber - b.gameNumber || a.ply - b.ply);
}
